/* eslint-disable class-methods-use-this */
/* eslint-disable no-extra-parens */
/* eslint-disable no-magic-numbers */
/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import * as _ from 'lodash';
import cx from 'classnames';
import i18next from 'i18next';

import { ProjectService } from '~config/services/project.service';
import { IirsaCountriesService } from '~config/services/iirsa_countries.service';

import styles from './styles.module.scss';

const ProjectsChartsComponent = (props) => {
    const { showSecondRow } = props;

    const [state, setState] = useState({
        countryChart: {},
        stageChart: {},
        sectorChart: {},
        sectorInvestmentChart: {},
        total: 0,
        lastMonthTotal: 0,
        showSecondRow: !!showSecondRow,
        countries: [],
        stageNameToIdMap: {},
        selectedCountries: [],  // Array of selected country names
        selectedStage: null,    // Selected stage name
        countryColorMap: {},    // Maps country names to colors
        stageColorMap: {}       // Maps stage names to colors
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            const cs = new IirsaCountriesService();
            const countriesResponse = await cs.get_la();

            setState(prev => ({
                ...prev,
                countries: countriesResponse.data
            }));

            // Fetch initial chart data without filters
            fetchFilteredData([], null, countriesResponse.data);
        };

        fetchInitialData();
    }, []);

    const sort = (data) => {
        function dataset(label, chartdata, datasetindex) {
            let i = 0;
            for (; i < chartdata.labels.length; i++) {
                if (label === chartdata.labels[i]) {
                    break;
                }
            }
            return chartdata.datasets[datasetindex][i];
        }

        if (data.datasets) {
            data.data = _.reduce(data.datasets, (acum, other) => _.zipWith(acum, other, (a, b) => Number.parseFloat(a) + Number.parseFloat(b)), _.map(data.labels, () => 0));
        }

        let labels = [];
        for (let i = 0; i < data.data.length; i++) {
            let newl = {};
            if (typeof data.labels[i] === 'object') {
                newl = { ...data.labels[i], value: data.data[i] };
            } else {
                newl = { label: data.labels[i], value: data.data[i] };
            }
            labels.push(newl);
        }
        labels = _.orderBy(labels, ['value'], 'desc');

        let retval = {};
        if (data.datasets) {
            labels = _.map(labels, l => l.label);
            retval = {
                labels,
                datasets: _.map(data.datasets, (ds, idx) => _.map(labels, l => dataset(l, data, idx))),
                datasetsLabels: data.datasetsLabels
            };
        } else if (typeof data.labels[0] === 'object') {
            retval = {
                labels, data: _.map(labels, l => l.value)
            };
        } else {
            retval = {
                labels: _.map(labels, l => l.label), data: _.map(labels, l => l.value)
            };
        }
        return retval;
    };

    const fetchFilteredData = async (selectedCountryNames, selectedStageName, countriesList = null) => {
        const ps = new ProjectService();
        const countries = countriesList || state.countries;

        // Build filter object
        const filters = {};

        // Convert country names to IDs
        if (selectedCountryNames && selectedCountryNames.length > 0) {
            const countryIds = selectedCountryNames
                .map(name => {
                    const country = countries.find(c => c.name === name);
                    return country ? country.id : null;
                })
                .filter(id => id !== null);

            if (countryIds.length > 0) {
                filters.iirsa_countries = countryIds;
            }
        }

        // Convert stage name to ID
        if (selectedStageName && state.stageNameToIdMap[selectedStageName]) {
            filters.iirsa_stage = state.stageNameToIdMap[selectedStageName];
        }

        // Fetch filtered chart data
        const chartDataResponse = await ps.get_chart_data(filters);
        processChartData(chartDataResponse.data, countries, selectedCountryNames, selectedStageName);
    };

    const processChartData = (data, countries, selectedCountryNames = [], selectedStageName = null) => {
        // Create stage name to ID mapping
        const stageNameToIdMap = {};
        if (data.stageChart && data.stageChart.labels) {
            data.stageChart.labels.forEach((label, index) => {
                stageNameToIdMap[label.name] = index + 1; // Stage IDs are 1-indexed
            });
        }

        // Sort data to get display order, then create color mappings based on sorted order
        const sortedCountryData = sort(data.countryChart);
        const sortedStageData = sort(data.stageChart);

        // Get labels in sorted order (descending by value)
        const sortedCountryLabels = sortedCountryData.labels || [];
        const sortedStageLabels = (sortedStageData.labels || []).map(l => l.label || l.name);

        // Create/update color mappings based on sorted order
        const countryColorMap = createColorMapping(
            sortedCountryLabels,
            state.countryColorMap
        );

        const stageColorMap = createColorMapping(
            sortedStageLabels,
            state.stageColorMap
        );

        setState(prev => ({
            ...prev,
            countryChart: formatAndOrderDataset(sort(data.countryChart), countryColorMap),
            stageChart: formatDatasetWithLabels(sort(data.stageChart), stageColorMap),
            sectorChart: formatCombinedDataset(sort(data.sectorChart)),
            sectorInvestmentChart: formatCombinedDataset(sort(data.sectorInvestmentChart)),
            total: data.total,
            lastMonthTotal: data.lastMonthTotal,
            showSecondRow: !!showSecondRow,
            countries,
            stageNameToIdMap,
            selectedCountries: selectedCountryNames,
            selectedStage: selectedStageName,
            countryColorMap,
            stageColorMap
        }));
    };

    const COLOR_PALETTE = [
        '#1FACDA',
        '#0E69B5',
        '#91C73D',
        '#FF675D',
        '#C6C0B7',
        '#1B5D24',
        '#FE9019',
        '#CD2C1E',
        '#E6E2C7',
        '#181718',
        '#FEE62D',
        '#E180AC',
    ];

    const createColorMapping = (items, existingMapping = {}) => {
        const newMapping = { ...existingMapping };
        let colorIndex = Object.keys(newMapping).length;

        items.forEach(item => {
            if (!newMapping[item]) {
                newMapping[item] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
                colorIndex++;
            }
        });

        return newMapping;
    };

    const formatAndOrderDataset = (chartData, colorMap = {}) => {
        if (chartData) {
            let countries = [];
            chartData.data.forEach((d, idx) => {
                countries.push({
                    data: d, label: chartData.labels[idx]
                });
            });
            countries = countries.sort((a, b) => b.data - a.data);

            // Use color map to assign consistent colors
            const colors = countries.map(c => colorMap[c.label] || COLOR_PALETTE[0]);

            return {
                datasets: [{
                    data: countries.map(c => c.data),
                    backgroundColor: colors
                }],
                labels: countries.map(c => c.label)
            };
        }
        return {};
    };

    const formatDatasetWithLabels = (chartData, colorMap = {}) => {
        if (!chartData || !chartData.labels) {
            return {};
        }

        const labels = chartData.labels.map(l => l.name);

        // Use color map to assign consistent colors
        const colors = labels.map(l => colorMap[l] || COLOR_PALETTE[0]);

        return {
            datasets: [{
                data: chartData.data,
                backgroundColor: colors
            }],
            labels: labels
        };
    };

    const formatCombinedDataset = (chartData) => {
        const datasets = [];
        chartData.datasetsLabels.forEach((ds_label, idx) => {
            datasets.push({
                label: ds_label,
                data: chartData.datasets[idx],
                backgroundColor: COLOR_PALETTE[(idx * 5) % COLOR_PALETTE.length]
            });
        });
        return { labels: chartData.labels, datasets };
    };

    const handleCountryChartClick = (event, elements) => {
        if (elements && elements.length > 0) {
            const elementIndex = elements[0]._index;
            const countryName = state.countryChart.labels[elementIndex];

            // Toggle country filter
            let newSelectedCountries;
            if (state.selectedCountries.includes(countryName)) {
                // Remove country if already selected
                newSelectedCountries = state.selectedCountries.filter(c => c !== countryName);
            } else {
                // Add country to selection
                newSelectedCountries = [...state.selectedCountries, countryName];
            }

            // Fetch data with new filters
            fetchFilteredData(newSelectedCountries, state.selectedStage);
        }
    };

    const handleStageChartClick = (event, elements) => {
        if (elements && elements.length > 0) {
            const elementIndex = elements[0]._index;
            const stageName = state.stageChart.labels[elementIndex];

            // Toggle stage filter (only one stage can be selected at a time)
            const newSelectedStage = state.selectedStage === stageName ? null : stageName;

            // Fetch data with new filters
            fetchFilteredData(state.selectedCountries, newSelectedStage);
        }
    };

    const clearCountryFilter = (countryName) => {
        const newSelectedCountries = state.selectedCountries.filter(c => c !== countryName);
        fetchFilteredData(newSelectedCountries, state.selectedStage);
    };

    const clearStageFilter = () => {
        fetchFilteredData(state.selectedCountries, null);
    };

    const countryChartOptions = {
        legend: { display: false },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true, stepSize: 1
                }
            }],
            xAxes: [{ display: true }]
        },
        onClick: handleCountryChartClick,
        onHover: (event, chartElement) => {
            event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        }
    };

    const stageChartOptions = {
        legend: { display: false },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true, stepSize: 1
                }
            }],
            xAxes: [{ display: true }]
        },
        onClick: handleStageChartClick,
        onHover: (event, chartElement) => {
            event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        }
    };

    const sectorChartOptions = {
        legend: { position: 'bottom', align: 'start' },
        scales: {
            xAxes: [{
                display: true, stacked: true, label: true, scaleLabel: {
                    display: true, labelString: i18next.t('projectCharts:subsector')
                }
            }],
            yAxes: [{
                stacked: true, ticks: { beginAtZero: true }, scaleLabel: {
                    display: true, labelString: i18next.t('projectCharts:numberOfProjects')
                }
            }]
        }
    };

    const sectorInvestmentChartOptions = {
        legend: { position: 'bottom', align: 'start' },
        tooltips: {
            callbacks: {
                label: tooltipItem => ` $ ${Math.trunc(tooltipItem.value)} ${i18next.t('projectCharts:millions')}`
            }
        },
        scales: {
            xAxes: [{
                display: true, stacked: true, label: true, scaleLabel: {
                    display: true, labelString: i18next.t('projectCharts:subsector')
                }
            }],
            yAxes: [{
                stacked: true, ticks: { beginAtZero: true }, scaleLabel: {
                    display: true, labelString: i18next.t('projectCharts:millionsUSD')
                }
            }]
        }
    };

    const hasActiveFilters = state.selectedCountries.length > 0 || state.selectedStage !== null;

    return (
        <>
            {hasActiveFilters && (
                <div className={styles.filterContainer}>
                    <div className={styles.filterLabel}>
                        {i18next.t('projectCharts:activeFilters', { defaultValue: 'Active Filters:' })}
                    </div>
                    <div className={styles.filterPills}>
                        {state.selectedCountries.map(country => (
                            <div key={country} className={styles.filterPill}>
                                <span>{i18next.t('projectCharts:country', { defaultValue: 'Country' })}: {country}</span>
                                <button
                                    className={styles.filterPillClose}
                                    onClick={() => clearCountryFilter(country)}
                                    aria-label={`Remove ${country} filter`}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {state.selectedStage && (
                            <div className={styles.filterPill}>
                                <span>{i18next.t('projectCharts:stage', { defaultValue: 'Stage' })}: {state.selectedStage}</span>
                                <button
                                    className={styles.filterPillClose}
                                    onClick={clearStageFilter}
                                    aria-label="Remove stage filter"
                                >
                                    ×
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className={styles.chartContainer}>
                <div className={cx(styles.firstColumn, styles.firstRow, styles.clickableChart)}>
                    <h3 className={styles.chartTitle}>{i18next.t('projectCharts:projectsByCountry')}</h3>
                    <Bar data={state.countryChart} options={countryChartOptions} />
                </div>

                <div className={cx(styles.secondColumn, styles.firstRow, styles.clickableChart)}>
                    <h3 className={styles.chartTitle}>{i18next.t('projectCharts:projectsByStage')}</h3>
                    <Bar data={state.stageChart} options={stageChartOptions} />
                </div>
                {state.showSecondRow ? (
                    <>
                        <div className={cx(styles.firstColumn, styles.secondRow)}>
                            <h3 className={styles.chartTitle}>{i18next.t('projectCharts:projectsBySector')}</h3>
                            <Bar data={state.sectorChart} options={sectorChartOptions} />
                        </div>

                        <div className={cx(styles.secondColumn, styles.secondRow)}>
                            <h3 className={styles.chartTitle}>{i18next.t('projectCharts:investmentBySector')}</h3>
                            <Bar data={state.sectorInvestmentChart} options={sectorInvestmentChartOptions} />
                        </div>
                    </>
                ) : null}
            </div>
        </>
    );
};

export default ProjectsChartsComponent;
